import {AsyncValidatorFn, FormGroup, ValidatorFn} from '@angular/forms';

// ─────────────────────────────────────────────────────────────────────────────
// Tipi primitivi
// ─────────────────────────────────────────────────────────────────────────────

/** Tutti i tipi di campo supportati dall'engine. */
export type FieldType =
  | 'text'
  | 'number'
  | 'email'
  | 'tel'
  | 'date'
  | 'password'
  | 'textarea'
  | 'dropdown'
  | 'multiselect'
  | 'radio'
  | 'checkbox'
  | 'acceptance'
  | 'module-toggle'
  | 'secret-state';

/**
 * Snapshot piatto di tutti i valori del form al momento corrente.
 * Chiave esterna = key della categoria (o ACCEPTANCE_GROUP_KEY), chiave interna = key del campo.
 * Viene passato alle funzioni `showIf` / `disabledIf` per calcoli condizionali.
 */
export type FormSnapshot = Record<string, Record<string, unknown>>;

// ─────────────────────────────────────────────────────────────────────────────
// LabelSegment  (etichette ricche con link)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Segmento di testo per etichette che contengono link cliccabili.
 *
 * Esempio:
 * ```ts
 * label: [
 *   { text: 'Ho letto e accetto i' },
 *   { text: 'Termini e Condizioni', url: '/termini' },
 *   { text:' e la' },
 *   { text: 'Privacy Policy', url: 'https://acme.it/privacy', external: true },
 * ]
 * ```
 */
export interface LabelSegment {
  /** Testo del segmento. */
  text: string;

  /**
   * URL del link. Se assente, il segmento è testo semplice.
   * Può essere un path relativo ('/termini') o un URL assoluto.
   */
  url?: string;

  /**
   * Se true, apre il link in una nuova scheda (target="_blank").
   * Default: false.
   */
  external?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// AcceptanceCheckboxConfig
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configurazione di una singola checkbox di accettazione (T&C, Privacy, ecc.).
 *
 * Può essere usata in due modi:
 * 1. In `DynamicFormConfig.acceptances[]` → resa fuori dall'accordion, prima del submit.
 * 2. In `CategoryConfig.fields[]` con `type: 'acceptance'` tramite `FieldConfig.acceptance`.
 */
export interface AcceptanceCheckboxConfig {
  /**
   * Chiave univoca nel FormGroup delle accettazioni.
   * Nel FormSnapshot sarà accessibile come `value[ACCEPTANCE_GROUP_KEY][key]`.
   */
  key: string;

  /**
   * Etichetta della checkbox.
   * - `string`: testo semplice.
   * - `LabelSegment[]`: testo con link cliccabili intercalati.
   */
  label: string | LabelSegment[];

  /**
   * Se true (default), blocca il submit finché la checkbox non è spuntata.
   * Corrisponde a `Validators.requiredTrue`.
   */
  required?: boolean;

  /** Valore iniziale. Default: false. */
  defaultChecked?: boolean;

  /**
   * Funzione pura: se ritorna false il campo viene nascosto,
   * il suo controllo disabilitato e i validatori rimossi.
   */
  showIf?: (snapshot: FormSnapshot) => boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// SecretStateConfig  (stato di un segreto già registrato)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Testi della card `secret-state`, che dichiara l'esistenza di un segreto già
 * registrato (password, PIN) e fa da interruttore per sostituirlo.
 *
 * Il controllo associato è un booleano: `false` = "tieni quello registrato",
 * `true` = "sto inserendo un valore nuovo". I campi del segreto e della sua
 * conferma vivono accanto alla card con uno `showIf` che legge questo stesso
 * valore, così finché è `false` non vengono resi e l'engine ne rimuove i
 * validatori.
 *
 * Tutti i testi hanno un default: vanno passati solo per declinarli sul
 * segreto specifico (es. "Impostato" al maschile per il PIN).
 */
export interface SecretStateConfig {
  /** Riga di stato quando il segreto è registrato. Default: 'Impostata'. */
  statusText?: string;

  /**
   * Riga di stato mentre l'utente sta inserendo il valore nuovo.
   * Default: 'Inserisci il nuovo valore nei campi qui sotto'.
   */
  editingText?: string;

  /** Etichetta del pulsante che apre i campi. Default: 'Modifica'. */
  actionLabel?: string;

  /** Etichetta del pulsante che li richiude. Default: 'Annulla'. */
  cancelLabel?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldOption (per dropdown / radio / checkbox con oggetti)
// ─────────────────────────────────────────────────────────────────────────────

export interface FieldOption {
  label: string;
  value: unknown;
  disabled?: boolean;
  displayValue?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldConfig
// ─────────────────────────────────────────────────────────────────────────────

export interface FieldConfig {
  /** Chiave univoca all'interno della categoria. */
  key: string;

  /** Tipo di input da renderizzare. */
  type: FieldType;

  /**
   * Etichetta del campo; usata anche come placeholder.
   */
  label: string;

  /**
   * Se true, mostra asterisco rosso all'etichetta.
   * La validazione reale è garantita da Validators.required nei validatori.
   */
  required?: boolean;

  /** Path icona SVG opzionale. Es: '/icons/user.svg' */
  icon?: string;

  /**
   * Testo descrittivo opzionale, mostrato sotto la label.
   * Usato al momento solo da `type === 'module-toggle'`.
   */
  description?: string;

  /**
   * Se valorizzato, mostra un'icona informativa accanto al campo che, al
   * click, apre un popup con il testo associato a questo `id_info`.
   * Il testo è risolto tramite il provider FIELD_INFO_RESOLVER (opzionale).
   */
  infoId?: number;

  /** Valore iniziale se non arriva dal prefill. */
  defaultValue?: unknown;

  /**
   * Opzioni per dropdown / radio / checkbox.
   * Può essere array di stringhe, numeri o FieldOption.
   */
  options?: FieldOption[] | string[] | number[];

  /**
   * Alternativa dinamica a `options`.
   * Se presente, viene chiamata ad ogni cambio di snapshot e il risultato
   * sovrascrive `options` per quel campo.
   * Stessa firma di `showIf` e `disabledIf` — riceve lo snapshot corrente.
   */
  optionsFn?: (snapshot: FormSnapshot) => FieldOption[];

  /** Validatori sincroni Angular. */
  validators?: ValidatorFn[];

  /** Validatori asincroni (es. verifica P.IVA via API). */
  asyncValidators?: AsyncValidatorFn[];

  /**
   * Funzione pura: se ritorna false il campo viene nascosto,
   * il suo controllo disabilitato e i validatori rimossi.
   */
  showIf?: (snapshot: FormSnapshot) => boolean;

  /**
   * Funzione pura: se ritorna true il campo è visibile ma disabilitato.
   */
  disabledIf?: (snapshot: FormSnapshot) => boolean;

  /**
   * Usato solo quando `type === 'acceptance'`.
   * Contiene la configurazione della checkbox di consenso da rendere
   * all'interno di questa posizione nella categoria.
   * Se non specificato con type 'acceptance', il campo è ignorato.
   */
  acceptance?: AcceptanceCheckboxConfig;

  /**
   * Usato solo quando `type === 'secret-state'`.
   * Personalizza i testi della card; se omesso valgono i default.
   */
  secretState?: SecretStateConfig;

  /**
   * Usato solo quando `type === 'radio'`.
   *
   * Se true il riquadro si stringe sul contenuto invece di occupare tutta la
   * colonna. Serve ai gruppi di poche opzioni brevi, dove il bordo a piena
   * larghezza lascia dietro l'ultima opzione un vuoto che sembra un campo da
   * riempire. Il campo continua a occupare la sua cella nella griglia: cambia
   * la larghezza del bordo, non il posto che il campo prende nel layout.
   *
   * Su viewport strette il riquadro non sfora comunque la colonna, e le
   * opzioni vanno a capo come sempre.
   */
  fitContent?: boolean;

  /**
   * Usato solo quando `type === 'radio'`.
   *
   * Se true ogni opzione è un riquadro a sé, con l'etichetta a sinistra e il
   * pallino a destra, e le opzioni si dividono la riga in parti uguali. Serve
   * quando la scelta è l'unico contenuto della sezione e il titolo della
   * sezione già dice cosa si sta scegliendo: l'etichetta del campo resta solo
   * per gli screen reader.
   */
  radioCards?: boolean;

  /**
   * Usato solo quando `type === 'dropdown'`.
   * Testo mostrato nel campo finché non è scelta alcuna opzione: indica come
   * va usato il campo quando lasciarlo vuoto è una scelta valida.
   */
  placeholder?: string;

  /**
   * Usato solo quando `type === 'checkbox'` con `options` (gruppo di checkbox).
   * Se true, mostra sopra l'elenco un filtro di visualizzazione a scelta
   * singola (Tutte / Solo associate / Solo non associate) che restringe
   * le opzioni mostrate senza modificare il valore selezionato.
   *
   * "Associate" sono le opzioni selezionate all'apertura del form (lo stato
   * salvato, dal prefill), non le spunte correnti: modificare le spunte non
   * sposta le righe da un gruppo all'altro finché non si salva.
   */
  checkboxFilter?: boolean;

  /**
   * Usato solo quando `type === 'checkbox'` con `options` (gruppo di checkbox).
   * Se true, mostra una checkbox isolata "Seleziona tutte" che spunta o
   * despunta in blocco TUTTE le opzioni (indipendentemente dal filtro attivo).
   */
  checkboxSelectAll?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// CategoryConfig
// ─────────────────────────────────────────────────────────────────────────────

export interface CategoryConfig {
  /** Chiave univoca; diventa il nome del FormGroup figlio. */
  key: string;

  /** Titolo mostrato nell'header dell'accordion. */
  title: string;

  /** Lista ordinata dei campi. */
  fields: FieldConfig[];

  /**
   * Se true i campi della categoria si dispongono uno sotto l'altro a piena
   * larghezza, invece che su due colonne.
   */
  singleColumn?: boolean;

  /**
   * Se ritorna false, la categoria intera è nascosta e tutti
   * i suoi campi vengono disabilitati.
   */
  showIf?: (snapshot: FormSnapshot) => boolean;

  /**
   * Validatori sincroni Angular.
   * Utilizzato per controlli cross tra i campi
   * (esempio: campi nome e cognome corrispondenti a codice fiscale).
   */
  validators?: ValidatorFn[];

  /**
   * Effetti collaterali cross-field eseguiti a ogni cambio di snapshot,
   * dopo la sincronizzazione condizionale dei controlli.
   * Usare per autofill, reset a cascata, setValue programmatici tra campi.
   *
   * IMPORTANTE: usare sempre { emitEvent: false } nelle setValue per evitare loop.
   */
  sideEffects?: (snapshot: FormSnapshot, group: FormGroup) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// DynamicFormConfig
// ─────────────────────────────────────────────────────────────────────────────

export interface DynamicFormConfig {
  /**
   * ID univoco del form; usato come chiave sessionStorage per il draft.
   * Es: 'anagrafica-cliente', 'fattura-2024'
   */
  formId: string;

  /** Lista ordinata delle categorie (sezioni accordion). */
  categories: CategoryConfig[];

  /**
   * Messaggio custom per la modale di conferma uscita.
   * Default: 'Sei sicuro di voler uscire? Perderai tutti i dati compilati.'
   */
  unsavedChangesMessage?: string;

  /**
   * Se true (default), carica il draft dal sessionStorage all'apertura.
   * Il draft ha precedenza sul prefill dal backend.
   */
  autoLoadDraft?: boolean;

  /**
   * Intervallo ms per il salvataggio automatico del draft.
   * Default: 30000. Impostare a 0 per disabilitare.
   */
  draftIntervalMs?: number;

  /**
   * Checkbox di accettazione da mostrare FUORI dall'accordion,
   * in un blocco dedicato tra le categorie e il pulsante "Invia".
   *
   * Ogni checkbox genera un FormControl nel gruppo `ACCEPTANCE_GROUP_KEY`
   * del rootGroup. Il submit è bloccato se una qualsiasi checkbox
   * obbligatoria non è spuntata.
   *
   * Esempio:
   * ```ts
   * acceptances: [
   *   {
   *     key: 'termini',
   *     label: [
   *       { text: 'Accetto i' },
   *       { text: 'Termini e Condizioni', url: '/termini' },
   *     ],
   *     required: true,
   *   },
   *   {
   *     key: 'marketing',
   *     label: 'Acconsento alle comunicazioni promozionali',
   *     required: false, // facoltativa
   *   },
   * ]
   * ```
   */
  acceptances?: AcceptanceCheckboxConfig[];

  /**
   * Titolo opzionale per la sezione delle accettazioni globali.
   * Se non specificato, viene usato un default (es. "Accettazioni e consensi").
   */
  acceptancesSectionTitle?: string;

  /**
   * Descrizione opzionale per la sezione delle accettazioni globali.
   * Se non specificato, NON viene mostrato un default.
   */
  acceptancesSectionDescription?: string;

  /**
   * Nota informativa mostrata sotto le checkbox di accettazione.
   */
  acceptancesSectionNote?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Costante chiave del FormGroup accettazioni
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Chiave riservata nel rootGroup per il FormGroup delle accettazioni globali.
 * Non usare questa stringa come key di una categoria.
 */
export const ACCEPTANCE_GROUP_KEY = '__acceptances__' as const;

/**
 * Chiave riservata nello snapshot per i valori iniziali (prefill / default).
 * Non usare questa stringa come key di una categoria.
 */
export const INITIAL_VALUES_KEY = '__initial__' as const;
